// src/algorithms/bottleneckDiagnosis.ts - UPDATED VERSION
import { Project } from '../types';
import { mockProjects } from '../data/mockData';

export interface BottleneckDiagnosisInput {
  projectId: string;
}

export interface BottleneckDiagnosisOutput {
  rootCauseAnalysis: {
    primaryCause: {
      category: 'people' | 'process' | 'technical' | 'dependencies';
      description: string;
      confidence: number;
      impactPercentage: number;
      evidence: string[];
    };
    secondaryCauses: Array<{
      category: string;
      description: string;
      impactPercentage: number;
    }>;
    confidenceScore: number;
  };
  impactAssessment: {
    currentVelocity: number;
    expectedVelocity: number;
    gapPercentage: number;
    timelineDelayWeeks: number;
    financialImpact: number;
  };
  recommendations: Array<{
    action: string;
    category: 'immediate' | 'short-term' | 'long-term';
    effort: 'low' | 'medium' | 'high';
    expectedImprovement: number;
    owner: string;
  }>;
  visualizationData: {
    velocityTrend: Array<{ week: number; velocity: number }>;
    causeBreakdown: Array<{ category: string; value: number }>;
    impactTimeline: Array<{ scenario: string; weeks: number }>;
  };
}

export function diagnoseBottlenecks(
  input: BottleneckDiagnosisInput
): BottleneckDiagnosisOutput {
  const project = mockProjects.find(p => p.id === input.projectId);
  
  if (!project) {
    throw new Error('Project not found');
  }

  // Enhanced analysis based on actual project data
  const causes = analyzeCausesWithRealData(project);
  const impact = calculateRealisticImpact(project, causes);
  const recommendations = generateDataDrivenRecommendations(project, causes);

  return {
    rootCauseAnalysis: {
      primaryCause: causes.primary,
      secondaryCauses: causes.secondary,
      confidenceScore: calculateDataDrivenConfidence(project, causes),
    },
    impactAssessment: impact,
    recommendations,
    visualizationData: generateRealisticVisualizationData(project, causes, impact),
  };
}

function analyzeCausesWithRealData(project: Project): {
  primary: BottleneckDiagnosisOutput['rootCauseAnalysis']['primaryCause'];
  secondary: BottleneckDiagnosisOutput['rootCauseAnalysis']['secondaryCauses'];
} {
  const causes = [];
  
  // Analyze based on real project data
  const teamSize = project.teamMembers?.length || 3;
  const requiredTeamSize = project.complexity === 'high' ? 8 : 
                          project.complexity === 'medium' ? 6 : 4;
  
  // People-related causes
  if (teamSize < requiredTeamSize) {
    const gap = requiredTeamSize - teamSize;
    causes.push({
      category: 'people' as const,
      description: `Understaffed: ${gap} team members short of required ${requiredTeamSize}`,
      impactPercentage: Math.min(70, gap * 15),
    });
  }
  
  // Process-related causes
  const blockerCount = project.blockers?.length || 0;
  if (blockerCount > 2) {
    causes.push({
      category: 'process',
      description: `Process inefficiencies: ${blockerCount} active blockers`,
      impactPercentage: Math.min(60, blockerCount * 15),
    });
  }
  
  // Technical causes
  const techDebtLevel = project.techDebt || 3;
  if (techDebtLevel > 5) {
    causes.push({
      category: 'technical',
      description: `Technical debt: Level ${techDebtLevel}/10 requires attention`,
      impactPercentage: techDebtLevel * 8,
    });
  }
  
  // Dependency causes
  const dependencyCount = project.dependencies?.length || 0;
  if (dependencyCount > 1) {
    causes.push({
      category: 'dependencies',
      description: `External dependencies: Blocked by ${dependencyCount} other projects`,
      impactPercentage: Math.min(50, dependencyCount * 12),
    });
  }
  
  // Add timeline pressure cause if project is delayed
  if (project.timelineDelay && project.timelineDelay > 2) {
    causes.push({
      category: 'process',
      description: `Timeline pressure: ${project.timelineDelay} weeks behind schedule`,
      impactPercentage: project.timelineDelay * 10,
    });
  }
  
  // Sort by impact and pick primary
  causes.sort((a, b) => b.impactPercentage - a.impactPercentage);
  
  const primary = causes[0] || {
    category: 'process' as const,
    description: 'Multiple contributing factors',
    impactPercentage: 40,
  };
  
  return {
    primary: {
      ...primary,
      confidence: calculateRealisticConfidence(project, primary),
      evidence: gatherRealEvidence(project, primary.category),
    },
    secondary: causes.slice(1),
  };
}

function calculateRealisticConfidence(project: Project, cause: any): number {
  let confidence = 75; // Base confidence
  
  // Increase confidence based on data quality
  if (project.velocity !== undefined) confidence += 10;
  if (project.timelineDelay !== undefined) confidence += 5;
  if (project.teamMembers?.length > 0) confidence += 5;
  
  // Adjust based on project status
  if (project.status === 'red') confidence += 10;
  if (project.status === 'green') confidence -= 5;
  
  return Math.min(95, confidence);
}

function gatherRealEvidence(project: Project, category: string): string[] {
  const evidence: string[] = [];
  
  switch (category) {
    case 'people':
      evidence.push(`Team size: ${project.teamMembers?.length || 3} members`);
      evidence.push(`Required size: ${project.complexity === 'high' ? 8 : 
                    project.complexity === 'medium' ? 6 : 4} members`);
      if (project.velocity !== undefined) {
        evidence.push(`Current velocity: ${project.velocity} (target: 75)`);
      }
      break;
    case 'process':
      evidence.push(`Active blockers: ${project.blockers?.length || 0}`);
      evidence.push(`Dependencies: ${project.dependencies?.length || 0} projects`);
      break;
    case 'technical':
      evidence.push(`Tech debt level: ${project.techDebt || 3}/10`);
      evidence.push(`Complexity: ${project.complexity || 'medium'}`);
      break;
    case 'dependencies':
      evidence.push(`External dependencies: ${project.dependencies?.length || 0}`);
      evidence.push(`Critical path: ${project.isCriticalPath ? 'Yes' : 'No'}`);
      break;
  }
  
  return evidence;
}

function calculateRealisticImpact(
  project: Project,
  causes: any
): BottleneckDiagnosisOutput['impactAssessment'] {
  const currentVelocity = project.velocity || 50;
  const expectedVelocity = 75; // Industry standard for healthy projects
  const gap = expectedVelocity - currentVelocity;
  
  // Calculate timeline delay based on multiple factors
  const baseDelay = causes.primary.impactPercentage / 10; // 10% impact = 1 week delay
  const teamSizeFactor = (project.teamMembers?.length || 3) < 4 ? 1.5 : 1;
  const complexityFactor = project.complexity === 'high' ? 1.8 : 
                          project.complexity === 'medium' ? 1.3 : 1;
  
  const delayWeeks = Math.round(baseDelay * teamSizeFactor * complexityFactor * 10) / 10;
  
  // Calculate financial impact
  const teamCostPerWeek = (project.teamMembers?.length || 3) * 2500; // $2.5k per person per week
  const financialImpact = delayWeeks * teamCostPerWeek;
  
  return {
    currentVelocity,
    expectedVelocity,
    gapPercentage: Math.round((gap / expectedVelocity) * 100),
    timelineDelayWeeks: Math.max(1, Math.min(12, delayWeeks)), // Clamp between 1-12 weeks
    financialImpact: Math.round(financialImpact / 1000) * 1000, // Round to nearest thousand
  };
}

function generateDataDrivenRecommendations(
  project: Project,
  causes: any
): BottleneckDiagnosisOutput['recommendations'] {
  const recommendations: BottleneckDiagnosisOutput['recommendations'] = [];
  
  // Immediate actions based on project status
  if (project.status === 'red') {
    recommendations.push({
      action: 'Initiate emergency daily standups with executive visibility',
      category: 'immediate',
      effort: 'low',
      expectedImprovement: 20,
      owner: 'Project Manager',
    });
  }
  
  // Recommendations based on primary cause
  switch (causes.primary.category) {
    case 'people':
      const teamGap = (project.complexity === 'high' ? 8 : 
                       project.complexity === 'medium' ? 6 : 4) - (project.teamMembers?.length || 3);
      if (teamGap > 0) {
        recommendations.push({
          action: `Add ${teamGap} team members with ${project.requiredSkills?.[0]?.skill || 'relevant'} skills`,
          category: 'short-term',
          effort: 'medium',
          expectedImprovement: 25,
          owner: 'Engineering Manager',
        });
      }
      break;
      
    case 'process':
      recommendations.push({
        action: 'Implement RACI matrix and streamline approval process',
        category: 'immediate',
        effort: 'medium',
        expectedImprovement: 30,
        owner: 'Process Owner',
      });
      break;
      
    case 'technical':
      recommendations.push({
        action: `Allocate ${project.techDebt && project.techDebt > 7 ? '40%' : '20%'} sprint capacity to tech debt`,
        category: 'short-term',
        effort: 'high',
        expectedImprovement: 35,
        owner: 'Tech Lead',
      });
      break;
      
    case 'dependencies':
      recommendations.push({
        action: 'Establish weekly dependency sync with blocking teams',
        category: 'immediate',
        effort: 'low',
        expectedImprovement: 25,
        owner: 'Program Manager',
      });
      break;
  }
  
  // Always include monitoring recommendation
  recommendations.push({
    action: 'Implement real-time dashboard with velocity and blocker tracking',
    category: 'long-term',
    effort: 'medium',
    expectedImprovement: 15,
    owner: 'Business Analyst',
  });
  
  return recommendations;
}

function calculateDataDrivenConfidence(project: Project, causes: any): number {
  let score = causes.primary.confidence;
  
  // Increase confidence for multiple converging causes
  if (causes.secondary.length >= 2) {
    score += 10;
  }
  
  // Increase confidence if data is recent
  const daysSinceUpdate = project.lastUpdated ? 
    (new Date().getTime() - new Date(project.lastUpdated).getTime()) / (1000 * 60 * 60 * 24) : 30;
  if (daysSinceUpdate < 7) {
    score += 15;
  }
  
  return Math.min(100, score);
}

function generateRealisticVisualizationData(
  project: Project,
  causes: any,
  impact: any
): BottleneckDiagnosisOutput['visualizationData'] {
  // Generate velocity trend based on actual project data
  const velocityTrend = [];
  const baseVelocity = project.velocity || 50;
  
  for (let week = 1; week <= 8; week++) {
    let velocity = baseVelocity;
    
    // Simulate velocity drop if project has issues
    if (project.status === 'red' && week > 2) {
      velocity -= (week - 2) * 8;
    } else if (project.status === 'yellow' && week > 3) {
      velocity -= (week - 3) * 5;
    }
    
    // Apply recovery if recommendations are implemented
    if (week > 5) {
      velocity += (week - 5) * 4;
    }
    
    velocityTrend.push({
      week,
      velocity: Math.max(30, Math.min(85, velocity)),
    });
  }
  
  // Generate cause breakdown
  const causeBreakdown = [
    { category: causes.primary.category, value: causes.primary.impactPercentage },
    ...causes.secondary.map((cause: any) => ({
      category: cause.category,
      value: cause.impactPercentage,
    })),
  ];
  
  // Generate impact timeline
  const impactTimeline = [
    { scenario: 'No intervention', weeks: Math.round(impact.timelineDelayWeeks * 1.5) },
    { scenario: 'Partial fixes', weeks: impact.timelineDelayWeeks },
    { scenario: 'Full resolution', weeks: Math.max(1, Math.round(impact.timelineDelayWeeks * 0.3)) },
  ];
  
  return {
    velocityTrend,
    causeBreakdown,
    impactTimeline,
  };
}